import { useEffect, useRef, useState } from "react";
import { Pause, Play, Volume2 } from "lucide-react";

interface CallAudioPlayerProps {
  audioUrl?: string | null;
  pauseTimestamps?: number[];
  durationSeconds?: number;
}

export const CallAudioPlayer = ({
  audioUrl,
  pauseTimestamps = [],
  durationSeconds,
}: CallAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(durationSeconds ?? 0);
  const [speed, setSpeed] = useState(1);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onDuration = () => setDuration(el.duration);
    const onEnded = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onDuration);
    el.addEventListener("ended", onEnded);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onDuration);
      el.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch(() => {});
    }
    setPlaying(!playing);
  };

  const seek = (pct: number) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    el.currentTime = pct * duration;
  };

  const setPlaybackSpeed = (s: number) => {
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };

  const fmt = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  if (!audioUrl) {
    return (
      <div className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
        <Volume2 className="w-4 h-4 text-slate-300 shrink-0" />
        <span className="text-[12px] text-slate-400">
          No recording available for this call.
        </span>
      </div>
    );
  }

  const progress = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Timeline */}
      <div
        className="relative h-8 cursor-pointer"
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          seek((e.clientX - rect.left) / rect.width);
        }}
      >
        {/* Track */}
        <div className="absolute top-1/2 left-0 right-0 h-1.5 -translate-y-1/2 rounded-full bg-slate-100">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${progress * 100}%`, background: "#3ECF8E" }}
          />
        </div>
        {/* Pause markers */}
        {duration > 0 &&
          pauseTimestamps.map((ms, i) => {
            const pct = ms / 1000 / duration;
            if (pct < 0 || pct > 1) return null;
            return (
              <div
                key={i}
                title={`Pause at ${fmt(ms / 1000)}`}
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border-2 border-amber-400 bg-white"
                style={{ left: `calc(${pct * 100}% - 4px)` }}
              />
            );
          })}
        {/* Playhead */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white border-2 shadow-sm"
          style={{
            left: `calc(${progress * 100}% - 6px)`,
            borderColor: "#3ECF8E",
          }}
        />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={togglePlay}
            className="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
            style={{ background: "#3ECF8E" }}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? (
              <Pause className="w-3.5 h-3.5 text-[#0F1629]" />
            ) : (
              <Play className="w-3.5 h-3.5 text-[#0F1629] ml-0.5" />
            )}
          </button>
          <span className="text-[11px] tabular-nums text-slate-500">
            {fmt(currentTime)} / {fmt(duration)}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {[1, 1.5, 2].map((s) => (
            <button
              key={s}
              onClick={() => setPlaybackSpeed(s)}
              className="text-[10px] font-semibold px-1.5 py-0.5 rounded transition-colors"
              style={{
                background: speed === s ? "#3ECF8E" : "transparent",
                color: speed === s ? "#0F1629" : "#94A3B8",
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
